import { Box, Skeleton, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import { ReactNode, useState, useEffect, useRef } from 'react';
import {
  StyledPanel,
  StyledScrollContent,
  StyledTab,
  StyledCustomTabsContainer,
  StyledTabText,
} from './styles';
import { TabItem } from '@/pages/municipality';

interface TabsProps {
  initialValue?: string | number;
  tabs: TabItem[];
  children?: ReactNode;

  showBackground?: boolean;
  loading?: boolean;
  onGetTabValue?: (value: TabItem) => void;
}

export const CustomTabs = ({
  tabs,
  children,
  showBackground,
  initialValue,
  loading,
  onGetTabValue,
}: TabsProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const [activeTab, setActiveTab] = useState(initialValue || tabs[0].value);

  useEffect(() => {
    if (initialValue !== undefined) {
      setActiveTab(initialValue);
    }
  }, [initialValue]);

  const handleTabClick = (tab: TabItem) => {
    setActiveTab(tab.value);
    onGetTabValue?.(tab);
  };

  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      <StyledCustomTabsContainer showBackground={showBackground}>
        {loading ? (
          <Skeleton animation="wave" height={48} width="100%" sx={{ transform: 'none' }} />
        ) : (
          <StyledScrollContent ref={scrollContainerRef}>
            {tabs.map((tab) => (
              <Tooltip key={tab.value} title={isMobile ? '' : tab.label}>
                <StyledTab onClick={() => handleTabClick(tab)} active={activeTab} tab={tab}>
                  <tab.icon />

                  <StyledTabText variant="button">{tab.label}</StyledTabText>
                </StyledTab>
              </Tooltip>
            ))}
          </StyledScrollContent>
        )}
      </StyledCustomTabsContainer>

      <StyledPanel showBackground={showBackground}>
        {children ??
          tabs.map((tab) => {
            if (tab.children && activeTab === tab.value) {
              return <Box key={tab.value}>{tab.children}</Box>;
            }
          })}

        {tabs.map(
          (tab) =>
            tab.value === activeTab && (
              <Box
                key={tab.value}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: theme.spacing(3),
                }}
              >
                {tab.tab}
              </Box>
            )
        )}
      </StyledPanel>
    </Box>
  );
};
